import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TERMS_VERSION } from "@/components/termsContent";
import { PRIVACY_VERSION } from "@/components/privacyContent";

export type ConsentContext = "signup" | "checkout" | "reaccept";

interface RecordConsentArgs {
  context: ConsentContext;
  /**
   * Optional explicit user id. When omitted (the common case at sign-up)
   * we read the currently authenticated user from Supabase. We need this
   * because the row is RLS-protected on `auth.uid() = user_id`, so the
   * insert can only succeed once the auth state has settled.
   */
  userId?: string;
}

/**
 * Records a durable click-wrap consent receipt for the current user.
 *
 * Failures are intentionally swallowed and only logged to the console:
 * a missing or temporarily unreachable `terms_acceptances` table must
 * never block sign-up or checkout. The receipt is best-effort evidence,
 * not a gate on the flow.
 */
export function useRecordConsent() {
  return useCallback(async ({ context, userId }: RecordConsentArgs) => {
    try {
      let resolvedUserId = userId;
      if (!resolvedUserId) {
        const { data } = await supabase.auth.getUser();
        resolvedUserId = data.user?.id;
      }
      if (!resolvedUserId) {
        console.warn(
          "[useRecordConsent] No authenticated user; skipping consent receipt"
        );
        return { error: new Error("No authenticated user") };
      }

      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : null;

      // The generated Supabase types do not yet include
      // `terms_acceptances` (regenerate with `supabase gen types typescript`
      // after applying the migration). Cast the table name to bypass the
      // missing typing without losing payload-level type safety.
      const { error } = await supabase
        .from("terms_acceptances" as never)
        .insert({
          user_id: resolvedUserId,
          terms_version: TERMS_VERSION,
          privacy_version: PRIVACY_VERSION,
          context,
          user_agent: userAgent,
        } as never);

      if (error) {
        console.warn("[useRecordConsent] Failed to record consent:", error);
      }
      return { error };
    } catch (err) {
      console.warn("[useRecordConsent] Unexpected error:", err);
      return { error: err };
    }
  }, []);
}
